import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LockKeyhole, NotebookPen, RefreshCw, Save, Plus } from 'lucide-react';
import './PersonMemoryEditor.css';

const failure = (error) => Object.values(error.response?.data?.errors || {}).flat().join(' ')
  || error.response?.data?.message || 'Saved memory is unavailable. Your edits are still here.';
const entry = (value) => ({ value, locked: false, source: null });

function initialDraft(view) {
  const data = view.data || {};
  const facts = { ...data.facts };
  for (const field of view.schema?.fields || []) facts[field.key] ||= entry(null);
  return { fields:[], facts, summary: data.summary || entry(null), notes: data.notes || entry([]), tags: data.tags || entry([]) };
}

/** API-independent component mirrored in WhatsApp; no credentials or customer IDs. */
export default function PersonMemoryEditor({ resourceKey, load, save, refreshVersion = 0, title = 'Contact memory' }) {
  const callbacks = useRef({ load, save });
  callbacks.current = { load, save };
  const generation = useRef(0);
  const active = useRef(resourceKey);
  const resourceEpoch = useRef(0);
  active.current = resourceKey;
  const dirtyRef = useRef(false);
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(0);
  const [changed, setChanged] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [latestAvailable, setLatestAvailable] = useState(false);
  const [newField,setNewField]=useState({key:'',label:'',type:'text'});

  const refresh = useCallback(async () => {
    const current = ++generation.current;
    const key = resourceKey;
    setLoading(true);
    try {
      const response = await callbacks.current.load();
      if (current !== generation.current || key !== active.current) return;
      setView(response);
      setError('');
      if (dirtyRef.current) {
        setLatestAvailable(true);
      } else {
        setDraft(initialDraft(response));
        setBaseline(response.revision);
        setLatestAvailable(false);
      }
    } catch (err) {
      if (current === generation.current && key === active.current) {
        if ([401, 403, 404].includes(err.response?.status)) {
          setView(null); setDraft(null); setChanged({}); dirtyRef.current = false;
        }
        setError(failure(err));
      }
    } finally {
      if (current === generation.current && key === active.current) setLoading(false);
    }
  }, [resourceKey]);

  useEffect(() => {
    resourceEpoch.current++;
    setNewField({key:'',label:'',type:'text'});
    generation.current++;
    dirtyRef.current = false;
    setView(null); setDraft(null); setChanged({}); setError(''); setNotice(''); setLatestAvailable(false); setSaving(false);
    const serial = generation;
    return () => { serial.current++; };
  }, [resourceKey]);
  useEffect(() => { refresh(); }, [refresh, refreshVersion]);

  // A typed value is protected from bot edits. Emptying a field hands it back to the bot
  // unless protection is ticked explicitly; a silent empty lock stops the bot saving answers.
  const edit = (section, key, value, locked = value !== null && value !== '') => {
    dirtyRef.current = true;
    setNotice('');
    const path = section === 'facts' ? 'facts.' + key : section;
    setChanged((current) => ({ ...current, [path]: true }));
    setDraft((current) => section === 'facts'
      ? { ...current, facts: { ...current.facts, [key]: { ...current.facts[key], value, locked } } }
      : { ...current, [section]: { ...current[section], value, locked } });
  };
  const reloadSaved = () => {
    if (!view) return;
    dirtyRef.current = false;
    setDraft(initialDraft(view)); setBaseline(view.revision); setChanged({}); setLatestAvailable(false); setError('');
    setNotice('Loaded the saved values.');
  };
  const submit = async (event) => {
    event.preventDefault();
    const key = resourceKey;
    const epoch = resourceEpoch.current;
    const payload = { expected_revision: baseline };
    for (const path of Object.keys(changed)) {
      if (path === 'fields') {
        payload.fields = draft.fields;
      } else if (path.startsWith('facts.')) {
        const field = path.slice(6);
        payload.facts ||= {};
        payload.facts[field] = { value: draft.facts[field].value || null, locked: draft.facts[field].locked };
      } else {
        const value = path === 'notes' ? [...new Set(draft.notes.value.map(note => note.trim()).filter(Boolean))] : draft[path].value;
        payload[path] = { value, locked: draft[path].locked };
      }
    }
    setSaving(true); setError(''); setNotice('');
    generation.current++; // A read started before this save cannot replace it.
    setLoading(false);
    try {
      const response = await callbacks.current.save(payload);
      if (key !== active.current || epoch !== resourceEpoch.current) return;
      generation.current++;
      setLoading(false);
      dirtyRef.current = false;
      setView(response); setDraft(initialDraft(response)); setBaseline(response.revision);
      setChanged({}); setLatestAvailable(false); setNotice('Memory saved.');
    } catch (err) {
      if (key === active.current && epoch === resourceEpoch.current) setError(failure(err));
    } finally {
      if (key === active.current && epoch === resourceEpoch.current) setSaving(false);
    }
  };
  const fields = new Map((view?.schema?.fields || []).map((field) => [field.key, field]));
  for (const field of draft?.fields || []) fields.set(field.key,{...field,scope:'contact'});
  const addField = () => {
    if (!newField.label.trim() || !/^[a-z][a-z0-9_]{0,39}$/.test(newField.key) || ['constructor','prototype','__proto__'].includes(newField.key)) {setError('Enter a label and a key using lowercase letters, numbers and underscores.');return;}
    if (fields.has(newField.key)) {setError('This field key already exists. Use the existing field or choose another key.');return;}
    if ([...fields.values()].filter(f=>f.scope === 'contact').length >= 20) {setError('This contact already has 20 contact-only fields.');return;}
    dirtyRef.current=true;setChanged(c=>({...c,fields:true}));setDraft(d=>({...d,fields:[...d.fields,{...newField,label:newField.label.trim()}],facts:{...d.facts,[newField.key]:entry(null)}}));setNewField({key:'',label:'',type:'text'});setError('');setNotice('Field added to your draft. Enter a value and save memory.');
  };
  for (const [key, value] of Object.entries(view?.data?.facts || {})) {
    if (!fields.has(key)) fields.set(key, { key, label: value.label || key, retired: true });
  }
  const tags = [...new Set([...(view?.schema?.allowed_tags || []), ...(draft?.tags.value || [])])];
  const protectedInput = (section, key, current, label) => <>
    <label className="person-memory-check">
      <input type="checkbox" checked={current.locked} onChange={(event) => edit(section, key, current.value, event.target.checked)} />
      <LockKeyhole size={13} />Protect {label} from bot edits
    </label>
    {current.locked && (current.value === null || current.value === '') && <small className="person-memory-hint">
      Empty and protected: the bot will not save an answer here. Untick to let it.</small>}
  </>;

  return <section className="person-memory" aria-label={title}>
    <header><div><NotebookPen size={19} /><h3>{title}</h3></div><button type="button" aria-label="Refresh saved memory" disabled={loading || saving} onClick={refresh}><RefreshCw size={16} /></button></header>
    <p className="person-memory-hint">Remembered for this person across conversations and bot changes. Values you type are protected from bot edits. Clearing a field lets the bot ask again.</p>
    {error && <p role="alert" className="person-memory-error">{error}</p>}
    {notice && <p role="status" className="person-memory-notice">{notice}</p>}
    {latestAvailable && <p className="person-memory-hint">Saved values have been refreshed. Your draft is still here. <button type="button" disabled={saving} onClick={reloadSaved}>Load saved values</button></p>}
    {!draft && !error && <p role="status">Loading saved memory…</p>}
    {draft && <form onSubmit={submit}>
      <fieldset disabled={saving}>
        <div className="person-memory-fields">
          {[...fields.values()].map((field) => {
            const current = draft.facts[field.key] || entry(null);
            return <div className="person-memory-field" key={field.key}>
              <label>{field.label}<input value={current.value ?? ''} maxLength={500} disabled={field.retired}
                placeholder="Not saved yet" onChange={(event) => edit('facts', field.key, event.target.value)} /></label>
              <div className="person-memory-field-meta"><small>{current.source === 'bot' ? 'Saved by bot' : current.source === 'human' ? 'Saved by team' : current.source === 'contact' ? 'From contact details' : current.source === 'legacy' ? 'From an earlier bot session' : 'Shared across bots'}{field.retired ? ' · No longer configured' : field.scope === 'contact' ? ' · This contact only' : ' · Organisation field'}</small>
                <button type="button" onClick={() => edit('facts', field.key, null)} aria-label={'Clear ' + field.label}>Clear</button></div>
              {protectedInput('facts', field.key, current, field.label)}
            </div>;
          })}
        </div>
        <details className="person-memory-add-field"><summary><Plus size={15}/>Add a field for this contact</summary><p>This field stays with this person across all bots. It will not appear on other contacts.</p><div><label>New field label<input maxLength={80} value={newField.label} placeholder="Preferred car model" onChange={e=>setNewField(f=>({...f,label:e.target.value,key:e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,'_').replace(/^_|_$/g,'').slice(0,40)}))}/></label><label>New field key<input maxLength={40} value={newField.key} placeholder="preferred_car_model" onChange={e=>setNewField(f=>({...f,key:e.target.value}))}/></label><label>New field type<select value={newField.type} onChange={e=>setNewField(f=>({...f,type:e.target.value}))}>{['text','name','number','date','email','phone'].map(t=><option key={t}>{t}</option>)}</select></label><button type="button" onClick={addField}><Plus size={14}/>Add contact field</button></div></details>
        {!fields.size && <p className="person-memory-hint">Define common fields in Auto Appy AI → Shared contact fields.</p>}
        <div className="person-memory-field"><label>Lead summary<textarea rows={3} maxLength={1500} value={draft.summary.value ?? ''}
          onChange={(event) => edit('summary', null, event.target.value || null)} placeholder="A short overview of this person and their needs" /></label>
          <small>Automatic summaries use the contact’s saved fields.</small>{protectedInput('summary', null, draft.summary, 'summary')}</div>
        <div className="person-memory-field"><label>Notes<textarea rows={4} value={draft.notes.value.join('\n')}
          onChange={(event) => edit('notes', null, event.target.value.split('\n'))} placeholder="One note per line" /></label>
          <small>Up to 10 notes, 500 characters each. Bot notes keep the person’s own wording.</small>{protectedInput('notes', null, draft.notes, 'notes')}</div>
        <div className="person-memory-field"><strong>Saved tags</strong><div className="person-memory-tags">{tags.map((tag) => <label key={tag}>
          <input type="checkbox" checked={draft.tags.value.includes(tag)} onChange={(event) => edit('tags', null,
            event.target.checked ? [...draft.tags.value, tag] : draft.tags.value.filter((value) => value !== tag))} />{tag}</label>)}</div>
          {!tags.length && <small>Define tags in the shared contact fields settings.</small>}
          {protectedInput('tags', null, draft.tags, 'tags')}</div>
        <div className="person-memory-actions"><button className="person-memory-save" type="submit" disabled={!Object.keys(changed).length || saving}><Save size={15} />{saving ? 'Saving…' : 'Save memory'}</button>
          {Object.keys(changed).length > 0 && <span>Unsaved changes</span>}</div>
      </fieldset>
    </form>}
    {!!view?.events?.length && <details><summary>Recent memory changes</summary><ul>{view.events.map((event) => <li key={event.id}>
      <strong>{({bot:'Bot',human:'Team',contact:'Contact details',legacy:'Earlier session'})[event.source] || 'Team'}</strong> · {event.changed_keys.map(key => key.replace('facts.', '')).join(', ')}
      <time>{new Date(event.created_at).toLocaleString()}</time>
    </li>)}</ul></details>}
  </section>;
}
